import { useEffect, useState } from "react";
import { RefreshCw, Instagram, Eye, EyeOff, Star, StarOff, Trash2 } from "lucide-react";
import { crm } from "../lib/crmClient";

export default function CrmInstagram() {
    const [cfg, setCfg] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ instagram_business_account_id: "", access_token: "", handle: "paint_king" });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [showSetup, setShowSetup] = useState(false);

    const refresh = () =>
        Promise.all([
            crm.get("/crm/instagram/config"),
            crm.get("/crm/instagram/posts?limit=60"),
        ]).then(([a, b]) => {
            setCfg(a.data);
            setPosts(b.data);
        }).finally(() => setLoading(false));

    useEffect(() => { refresh(); }, []);

    const connect = async (e) => {
        e.preventDefault();
        setSaving(true); setMsg("");
        try {
            const { data } = await crm.post("/crm/instagram/config", form);
            setMsg(data.sync?.ok ? `✓ Connected · pulled ${data.sync.count} posts.` : `Connected, but sync failed: ${data.sync?.reason || "unknown"}`);
            setForm({ ...form, access_token: "" });
            await refresh();
        } catch (err) {
            setMsg(err?.response?.data?.detail?.toString() || "Could not save");
        } finally { setSaving(false); }
    };

    const sync = async () => {
        setSaving(true); setMsg("");
        try {
            const { data } = await crm.post("/crm/instagram/sync");
            setMsg(data.ok ? `✓ Synced ${data.count} posts.` : `Sync failed: ${data.reason}`);
            await refresh();
        } finally { setSaving(false); }
    };

    const toggle = async (id, kind, value) => {
        await crm.patch(`/crm/instagram/posts/${id}/${kind}`, { value });
        refresh();
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect @paint_king from the website?")) return;
        await crm.post("/crm/instagram/disconnect");
        refresh();
    };

    if (loading) return <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/40">Loading…</div>;

    return (
        <div data-testid="crm-instagram">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">[ Instagram · @paint_king ]</div>
            <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mt-2">
                The <span className="rainbow-text">living</span> gallery.
            </h1>

            {/* Status */}
            <div data-testid="ig-status" className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="glass border border-white/15 p-5">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Status</div>
                    <div className={`font-display uppercase text-2xl mt-2 ${cfg?.connected ? "text-[#00FF85]" : "text-white/40"}`}>
                        {cfg?.connected ? "Connected" : "Disconnected"}
                    </div>
                </div>
                <div className="glass border border-white/15 p-5">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Last sync</div>
                    <div className="font-mono text-[12px] mt-2">{cfg?.last_sync_at ? new Date(cfg.last_sync_at).toLocaleString() : "—"}</div>
                    {cfg?.last_error && <div className="font-mono text-[10px] text-red-400 mt-2">{cfg.last_error}</div>}
                </div>
                <div className="glass border border-white/15 p-5">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Cached · Hidden · Pinned</div>
                    <div className="font-display uppercase text-2xl mt-2">{cfg?.cached_posts}·{cfg?.hidden_posts}·{cfg?.pinned_posts}</div>
                </div>
                <div className="glass border border-white/15 p-5 flex flex-col justify-between gap-3">
                    <button data-testid="ig-manual-sync" onClick={sync} disabled={saving || !cfg?.connected}
                        className="w-full inline-flex items-center justify-center gap-2 border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black transition-colors px-4 py-2.5 font-mono text-[11px] tracking-[0.28em] uppercase disabled:opacity-40">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh now
                    </button>
                    {cfg?.connected && (
                        <button onClick={disconnect} className="font-mono text-[10px] tracking-[0.28em] uppercase text-red-400 hover:text-red-300">
                            Disconnect
                        </button>
                    )}
                </div>
            </div>

            {msg && <div data-testid="ig-msg" className="mt-4 glass border border-[#FFE600]/40 bg-[#FFE600]/[0.06] px-4 py-3 font-mono text-[12px] text-[#FFE600]">{msg}</div>}

            {/* Setup form */}
            <section className="mt-10">
                <button onClick={() => setShowSetup((v) => !v)} className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/60 hover:text-white">
                    {showSetup ? "▾" : "▸"} {cfg?.connected ? "Replace credentials" : "Connect Instagram"}
                </button>
                {(showSetup || !cfg?.connected) && (
                    <form onSubmit={connect} className="mt-4 glass border border-white/15 p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40 mb-2">Instagram Business Account ID</div>
                                <input data-testid="ig-id-input" required value={form.instagram_business_account_id}
                                    onChange={(e) => setForm({ ...form, instagram_business_account_id: e.target.value.trim() })}
                                    placeholder="17841…"
                                    className="w-full bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-3 py-2.5 font-mono text-[13px] text-white placeholder-white/30"/>
                            </label>
                            <label className="block">
                                <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40 mb-2">Long-lived Page access token</div>
                                <input data-testid="ig-token-input" required type="password" minLength={30}
                                    value={form.access_token}
                                    onChange={(e) => setForm({ ...form, access_token: e.target.value.trim() })}
                                    placeholder="EAA…"
                                    className="w-full bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-3 py-2.5 font-mono text-[13px] text-white placeholder-white/30"/>
                            </label>
                        </div>
                        <button data-testid="ig-connect" type="submit" disabled={saving}
                            className="mt-5 inline-flex items-center gap-2 border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black transition-colors px-6 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-40">
                            <Instagram className="w-4 h-4" /> {saving ? "Connecting…" : cfg?.connected ? "Replace & re-sync" : "Connect @paint_king"}
                        </button>
                        <SetupSteps />
                    </form>
                )}
            </section>

            {/* Posts moderation */}
            {posts.length > 0 && (
                <section className="mt-12">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 mb-4">[ Moderation · click a post to hide, pin, or open ]</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {posts.map((p) => {
                            const img = p.media_type === "VIDEO" ? p.thumbnail_url : p.media_url;
                            return (
                                <div key={p.id} data-testid={`ig-mod-${p.id}`} className={`relative aspect-square border overflow-hidden group ${p.hidden ? "opacity-30" : ""} ${p.pinned ? "border-[#FFE600]" : "border-white/10"}`}>
                                    {img && <img src={img} alt="" className="w-full h-full object-cover" />}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                                        <button onClick={() => toggle(p.id, "hidden", !p.hidden)} className="p-2 bg-white/15 hover:bg-white/30" title={p.hidden ? "Unhide" : "Hide"}>
                                            {p.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                        </button>
                                        <button onClick={() => toggle(p.id, "pinned", !p.pinned)} className="p-2 bg-white/15 hover:bg-white/30" title={p.pinned ? "Unpin" : "Pin"}>
                                            {p.pinned ? <StarOff className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
                                        </button>
                                        <a href={p.permalink} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/15 hover:bg-white/30" title="Open on Instagram">↗</a>
                                    </div>
                                    {p.pinned && <span className="absolute top-1 left-1 bg-[#FFE600] text-black px-1.5 py-0.5 font-mono text-[8px] tracking-[0.2em] uppercase">Pinned</span>}
                                    {p.hidden && <span className="absolute top-1 right-1 bg-red-500 text-white px-1.5 py-0.5 font-mono text-[8px] tracking-[0.2em] uppercase">Hidden</span>}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}

function SetupSteps() {
    return (
        <details className="mt-6 font-mono text-[12px] text-white/60 leading-relaxed">
            <summary className="cursor-pointer font-mono text-[10px] tracking-[0.28em] uppercase text-white/70 hover:text-white">▸ How to get the ID and token (5 min)</summary>
            <ol className="mt-4 space-y-2 list-decimal list-inside pl-1">
                <li>Make sure <strong>@paint_king</strong> is a <strong>Business or Creator</strong> account connected to a Facebook Page (in Instagram app → Settings → Account → Switch account type).</li>
                <li>Go to <a className="text-[#FFE600] underline" href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer">developers.facebook.com/apps</a> → Create App → <strong>Business</strong>. Add product <em>Facebook Login for Business</em>.</li>
                <li>Open <a className="text-[#FFE600] underline" href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer">Graph API Explorer</a>, pick your app, click <em>Get User Access Token</em>, and tick permissions: <code className="text-[#FFE600]">instagram_basic</code>, <code className="text-[#FFE600]">pages_show_list</code>, <code className="text-[#FFE600]">pages_read_engagement</code>.</li>
                <li>Run <code className="text-[#FFE600]">GET /me/accounts?fields=id,name,access_token,instagram_business_account</code>. Find the PaintKing Page row.</li>
                <li>Copy <code className="text-[#FFE600]">instagram_business_account.id</code> → paste above as the Account ID.</li>
                <li>Copy that row's <code className="text-[#FFE600]">access_token</code> → <em>exchange</em> it for a long-lived token via <code className="text-[#FFE600]">oauth/access_token?grant_type=fb_exchange_token</code>. Paste the long-lived token above.</li>
                <li>Click <em>Connect</em>. The gallery populates automatically and syncs every hour from here on.</li>
            </ol>
        </details>
    );
}
